import { useEffect, useRef, useState } from "react";

import Agents from "./Agents";
import Capabilities from "./Capabilities";
import Cta from "./Cta";
import DcPage, { useDcTheme } from "./DcPage";
import Faq from "./Faq";
import Hero from "./Hero";
import HowItWorks from "./HowItWorks";
import Platform from "./Platform";
import Trust from "./Trust";
import { AGENTS, DEFAULT_AGENT_ID } from "./content";
import { type ThemeName } from "./theme";
import { useHeroVideo } from "./useDcMotion";

/**
 * `AI2Aim Landing v2.dc.html`, ported.
 *
 * The chrome the canvas wraps around this — the root element, its eleven custom
 * properties, the fonts, the nav, the footer and the scroll runtime — now lives
 * in DcPage, because the About Us destinations need exactly the same shell. All
 * that is left here is the canvas's own state and its ten screens.
 *
 * The canvas exposes four editor props. Three are real and appear below with
 * the canvas's own defaults. The fourth, `cursorGlow`, is omitted: it gates a
 * code path that queries `[data-glow]`, an attribute that exists nowhere in the
 * markup, so the prop cannot affect anything rendered. See useDcMotion.ts.
 */

export interface LandingDcV2Props {
  defaultTheme?: ThemeName;
  parallaxIntensity?: number;
  /** Advances the agent panel every 4.2s until the visitor picks one. */
  agentAutoCycle?: boolean;
}

export default function LandingDcV2({
  defaultTheme = "dark",
  parallaxIntensity = 2,
  agentAutoCycle = false,
}: LandingDcV2Props) {
  return (
    <DcPage defaultTheme={defaultTheme} parallaxIntensity={parallaxIntensity}>
      <HomeScreens agentAutoCycle={agentAutoCycle} />
    </DcPage>
  );
}

/**
 * Split from the component above only so it sits *inside* the provider: the
 * hero cross-fades its video and starfield on the active theme, which it can
 * only read from a descendant of DcPage.
 */
function HomeScreens({ agentAutoCycle }: { agentAutoCycle: boolean }) {
  const { dark, vals } = useDcTheme();

  const heroRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [agent, setAgent] = useState(DEFAULT_AGENT_ID);
  const [trustOpen, setTrustOpen] = useState(0);
  const [faqOpen, setFaqOpen] = useState(0);

  useHeroVideo(videoRef, heroRef, dark);

  // Auto-cycle stops permanently the first time the visitor picks an agent —
  // the canvas latches `_picked` rather than merely deferring the next tick.
  const pickedRef = useRef(false);
  useEffect(() => {
    if (!agentAutoCycle) return;
    const iv = setInterval(() => {
      if (pickedRef.current) return;
      setAgent((current) => {
        const ids = AGENTS.map((a) => a.id);
        return ids[(ids.indexOf(current) + 1) % ids.length];
      });
    }, 4200);
    return () => clearInterval(iv);
  }, [agentAutoCycle]);

  const pickAgent = (id: string) => {
    pickedRef.current = true;
    setAgent(id);
  };

  return (
    <>
      <Hero vals={vals} videoRef={videoRef} dark={dark} ref={heroRef} />
      <Capabilities />
      <Platform />
      <HowItWorks />
      <Agents selectedId={agent} onPick={pickAgent} />
      {/* Both accordions collapse to -1 when the open row is clicked again. */}
      <Trust
        openIndex={trustOpen}
        onToggle={(i) => setTrustOpen(trustOpen === i ? -1 : i)}
      />
      <Faq
        openIndex={faqOpen}
        onToggle={(i) => setFaqOpen(faqOpen === i ? -1 : i)}
      />
      <Cta />
    </>
  );
}
